// js/claim-review-modal.js
// Adds a review step before the offer claim form submits: clicking "Review"
// (instead of submitting directly) validates the form natively, then opens a
// modal summarizing the entered values with Edit/Submit actions.
//
// #906: on a valid Review click, also fires a single (metered, session-cached
// server-side) AJAX check of the shipping address against MelissaData via the
// shared `validateAddress()` helper (see address-validator.js). If MelissaData
// suggests a correction that differs from what was entered, a "What You
// Entered" vs "What MelissaData Suggests" comparison is rendered inside the
// modal — showing only the differing field(s) — with two buttons letting the
// visitor choose which address to submit. On any failure, timeout, or
// no-difference result, the comparison is silently skipped and the modal
// behaves exactly as it did before this change: no error, no blocking, the
// existing plain summary is shown as-is.

import { validateAddress } from './address-validator';
import { createLogger } from './debug';

const { log, error: logError } = createLogger('claim-review-modal');

// Default (spinner) and rate-limited (#961) content for the shared
// `.claim-review-address-loading` element — kept as constants so
// showAddressValidationRateLimitNotice() and its reset in
// clearAddressComparison() can never drift out of sync with each other.
const LOADING_ICON_CLASS = 'fa-solid fa-spinner fa-spin';
const LOADING_TEXT = 'Checking your shipping address for suggested corrections…';
const RATE_LIMIT_ICON_CLASS = 'fa-solid fa-circle-info';
const RATE_LIMIT_TEXT = 'Address could not be validated right now — you can still submit your request as entered.';
// #974: same soft-notice pattern as the rate-limit notice above, shown when
// validateAddress() reports reason: 'captcha_pending' (the visitor's
// Turnstile verification hasn't landed server-side yet).
const CAPTCHA_PENDING_ICON_CLASS = 'fa-solid fa-circle-info';
const CAPTCHA_PENDING_TEXT = 'Verifying you\'re human… try again in a moment.';

// #1079: shown next to the Review button when the verification widget
// errors, times out, or never loads at all. Deliberately does not name
// Cloudflare/Turnstile — this is a public page for a general audience — and
// deliberately does not say "wait", because for a blocked script no amount
// of waiting can ever clear the condition.
const CAPTCHA_LOAD_FAILED_TEXT =
    'The security check on this page could not load — this is usually caused by an ad blocker or ' +
    'privacy extension. Please turn it off for this page and reload, or try a different browser.';

// How long to give the verification script to appear before assuming it was
// blocked outright (#1079). Turnstile's own data-error-callback /
// data-timeout-callback can only fire once its script has loaded, so the
// single most common failure — an ad blocker blocking the script's domain —
// fires neither. This watchdog covers that case. Generous enough not to
// misfire on a slow connection; if the widget verifies later, the notice is
// cleared by the success callback anyway.
const CAPTCHA_SCRIPT_LOAD_TIMEOUT_MS = 12000;

/**
 * Shipping-address fields checked against MelissaData's suggestion. Order
 * here also controls the order rows are rendered in the comparison section.
 *
 * #937: `country` is included here purely so it's carried through
 * buildEnteredAddress()/the Shipping Address summary group alongside the
 * other address fields — NOT so it can be "corrected" by MelissaData.
 * Country is an upfront, visitor-picked required choice (unlike the
 * free-text fields Melissa infers/corrects), so diffAddressFields() below
 * excludes it from the entered-vs-suggested comparison entirely.
 */
const ADDRESS_COMPARISON_FIELDS = [
    { key: 'address1', label: 'Address Line 1' },
    { key: 'address2', label: 'Address Line 2' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'postal_code', label: 'Postal Code' },
    { key: 'country', label: 'Country' },
];

/**
 * Fields eligible for the "What You Entered" vs "What MelissaData Suggests"
 * comparison UI — everything in ADDRESS_COMPARISON_FIELDS except `country`
 * (see the comment above it: a Melissa-suggested country must never override
 * the visitor's own Country select choice).
 */
const ADDRESS_DIFF_FIELDS = ADDRESS_COMPARISON_FIELDS.filter(({ key }) => key !== 'country');

function fieldValue(form, name) {
    const field = form.elements.namedItem(name);
    if (!field) return null;
    return (field.value || '').trim();
}

function joinNonEmpty(parts, separator) {
    return parts.filter(part => part && part.trim() !== '').join(separator);
}

/**
 * Field groups for the review summary. Each row is either:
 * - { label, field }              simple field, or { ..., checkbox: true } for Yes/No
 * - { label, build(form) }        computed single-line value
 * - { label, buildLines(form) }   computed multi-line value (rendered with <br>)
 *
 * A row is skipped if its underlying field(s) don't exist on the form at all
 * (defensive — the real claim form always has them). A group is skipped
 * entirely if none of its rows produced anything.
 */
const FIELD_GROUPS = [
    {
        heading: 'Your Information',
        rows: [
            {
                label: 'Name',
                requires: 'first_name',
                build: form => joinNonEmpty([
                    fieldValue(form, 'salutation'),
                    fieldValue(form, 'first_name'),
                    fieldValue(form, 'last_name'),
                ], ' ') || '—',
            },
        ],
    },
    {
        heading: 'Shipping Address',
        rows: [
            {
                label: 'Address',
                requires: 'address1',
                buildLines: (form, addressOverride) => {
                    // Prefer the override's value (entered or suggested,
                    // depending on the visitor's choice) when present; fall
                    // back to the live form field otherwise (#936).
                    //
                    // #937: `country` is the one exception — it always reads
                    // straight off the form's Country select, regardless of
                    // addressOverride. A Melissa-suggested address must never
                    // override the visitor's own upfront country choice, so
                    // the "Use suggested address" override never applies here.
                    const pick = key => {
                        if (key === 'country') return fieldValue(form, 'country');
                        return addressOverride && Object.prototype.hasOwnProperty.call(addressOverride, key)
                            ? (addressOverride[key] || '')
                            : fieldValue(form, key);
                    };

                    const cityStateZip = joinNonEmpty([
                        pick('city'),
                        joinNonEmpty([pick('state'), pick('postal_code')], ' '),
                    ], ', ');

                    return [
                        pick('address1'),
                        pick('address2'),
                        cityStateZip,
                        pick('country'),
                    ].filter(line => line && line !== '');
                },
            },
        ],
    },
    {
        heading: 'Contact Information',
        rows: [
            { label: 'Phone', field: 'phone' },
            { label: 'Email', field: 'email' },
        ],
    },
    {
        heading: 'About Your Request',
        rows: [
            { label: 'How will you use these issues?', field: 'notes' },
            { label: 'Quantity', field: 'quantity' },
            { label: 'Future offers', field: 'marketing_opt_in', checkbox: true },
        ],
    },
];

function buildRow(form, row, addressOverride) {
    if (row.requires && !form.elements.namedItem(row.requires)) return null;

    if (row.buildLines) {
        const lines = row.buildLines(form, addressOverride);
        return lines.length ? { label: row.label, lines } : null;
    }

    if (row.build) {
        return { label: row.label, value: row.build(form) };
    }

    const field = form.elements.namedItem(row.field);
    if (!field) return null;

    const value = row.checkbox ? (field.checked ? 'Yes' : 'No') : (fieldValue(form, row.field) || '—');
    return { label: row.label, value };
}

/**
 * Populate the modal's summary element with the form's current values.
 * Uses textContent throughout (never innerHTML for user-entered values) to
 * avoid any injection risk from names/notes the submitter typed; multi-line
 * values use real <br> elements rather than a string with embedded markup.
 *
 * `addressOverride` (#936), when provided, is a plain object keyed the same
 * way as `ADDRESS_COMPARISON_FIELDS` (address1/address2/city/state/
 * postal_code) — it's threaded down to ONLY the Shipping Address group's
 * `buildLines()` so the summary reflects whichever address (entered or
 * MelissaData-suggested) the visitor has chosen in the comparison UI above
 * it. `open()` still calls this with no override so the summary shows the
 * typed values immediately, before the async Melissa check resolves.
 */
function populateSummary(form, summaryEl, addressOverride = null) {
    summaryEl.innerHTML = '';

    FIELD_GROUPS.forEach(group => {
        const rows = group.rows.map(row => buildRow(form, row, addressOverride)).filter(Boolean);
        if (!rows.length) return;

        const heading = document.createElement('h4');
        heading.textContent = group.heading;
        summaryEl.appendChild(heading);

        const dl = document.createElement('dl');
        dl.className = 'claim-review-fields';

        rows.forEach(row => {
            const dt = document.createElement('dt');
            dt.textContent = row.label;

            const dd = document.createElement('dd');
            if (row.lines) {
                row.lines.forEach((line, index) => {
                    if (index > 0) dd.appendChild(document.createElement('br'));
                    dd.appendChild(document.createTextNode(line));
                });
            } else {
                dd.textContent = row.value;
            }

            dl.appendChild(dt);
            dl.appendChild(dd);
        });

        summaryEl.appendChild(dl);
    });
}

/**
 * Read the shipping-address fields currently on the form into a plain
 * object keyed the same way as the backend's `validated_address` payload,
 * so the two can be compared field-by-field.
 */
function buildEnteredAddress(form) {
    const address = {};
    ADDRESS_COMPARISON_FIELDS.forEach(({ key }) => {
        address[key] = fieldValue(form, key) || '';
    });
    return address;
}

/**
 * Case/whitespace-insensitive comparison — a suggestion that only differs in
 * case (e.g. "ca" vs "CA") isn't a meaningful correction worth surfacing.
 */
function normalizeForCompare(value) {
    return (value || '').trim().toLowerCase();
}

/**
 * Returns only the fields where the MelissaData suggestion actually differs
 * from what the visitor entered — an empty array means "no difference",
 * which the caller treats the same as a failed/unavailable validation (skip
 * the comparison UI entirely, show the plain summary as today).
 */
function diffAddressFields(entered, suggested) {
    return ADDRESS_DIFF_FIELDS.filter(({ key }) => (
        normalizeForCompare(entered[key]) !== normalizeForCompare(suggested[key])
    ));
}

/**
 * Create (once) the "What You Entered" vs "What MelissaData Suggests"
 * comparison section and insert it immediately before the plain summary, so
 * it reads as the first thing the visitor sees when a correction is found.
 * Hidden by default; populateAddressComparison()/clearAddressComparison()
 * toggle its contents and visibility on every Review click.
 *
 * Idempotent — safe to call on every Review click without appending
 * duplicate DOM, matching ensureValidationMessage()'s pattern above.
 */
/**
 * Build the "use original"/"use corrected" choice-toggle closure shared by
 * both the freshly-created and cached-element return branches of
 * ensureAddressComparisonSection(), so its behavior can never drift between
 * the two (see #933 — the cached branch previously omitted this entirely).
 *
 * #936: also gives the visitor visible feedback beyond the CSS `.is-selected`
 * swap (redundant, colorblind-safe `aria-pressed`) and re-renders the plain
 * summary below with whichever address was just chosen. `comparisonEls` is
 * passed in (rather than plain `entered`/`suggested` values) so this closure
 * always reads the *current* values off it — populateAddressComparison()
 * sets `comparisonEls.entered`/`.suggested` after this closure is built, and
 * clearAddressComparison() resets them back to null on the next Review
 * click, so a snapshot taken at closure-creation time would go stale.
 */
function buildSetChoice(form, useOriginalBtn, useCorrectedBtn, summaryEl, comparisonEls) {
    return choice => {
        const choiceField = form.elements.namedItem('address_validation_choice');
        if (choiceField) choiceField.value = choice;

        const isOriginal = choice === 'original';
        useOriginalBtn.classList.toggle('is-selected', isOriginal);
        useCorrectedBtn.classList.toggle('is-selected', !isOriginal);
        useOriginalBtn.setAttribute('aria-pressed', isOriginal ? 'true' : 'false');
        useCorrectedBtn.setAttribute('aria-pressed', isOriginal ? 'false' : 'true');

        const overrideAddress = isOriginal ? comparisonEls.entered : comparisonEls.suggested;
        populateSummary(form, summaryEl, overrideAddress);
    };
}

function ensureAddressComparisonSection(modal, summaryEl, form) {
    let section = modal.querySelector('.claim-review-address-comparison');
    if (section) {
        const loadingEl = section.querySelector('.claim-review-address-loading');
        const loadingIcon = loadingEl.querySelector('i');
        const loadingText = loadingEl.querySelector('span');
        const comparisonContent = section.querySelector('.claim-review-comparison-content');
        const enteredList = section.querySelector('.claim-review-comparison-entered');
        const suggestedList = section.querySelector('.claim-review-comparison-suggested');
        const useOriginalBtn = section.querySelector('.claim-review-use-original');
        const useCorrectedBtn = section.querySelector('.claim-review-use-corrected');

        // Build the returned object FIRST, then set `.setChoice` on it
        // referencing the same object — buildSetChoice()'s closure needs to
        // read live `.entered`/`.suggested` off `comparisonEls` over time,
        // not a snapshot taken before either was ever populated (#936).
        const comparisonEls = {
            section,
            loadingEl,
            loadingIcon,
            loadingText,
            comparisonContent,
            enteredList,
            suggestedList,
            useOriginalBtn,
            useCorrectedBtn,
            entered: null,
            suggested: null,
            loadingShowTimer: null,
        };
        comparisonEls.setChoice = buildSetChoice(form, useOriginalBtn, useCorrectedBtn, summaryEl, comparisonEls);

        return comparisonEls;
    }

    section = document.createElement('div');
    section.className = 'claim-review-address-comparison';
    section.style.display = 'none';
    // Announces the loading/result text changes below to screen-reader
    // users too — this is the same section used for both the "Checking your
    // address…" placeholder and the eventual comparison result, so one
    // live region covers both.
    section.setAttribute('aria-live', 'polite');

    // Placeholder shown (after a short delay — see showAddressValidationLoading())
    // while the MelissaData check is in flight, so a visitor on a slow
    // connection knows something is happening instead of seeing an empty gap
    // above the summary. Lives at the top of this same section (rather than
    // a separate element) so there's no layout jump when it's replaced by
    // either the comparison grid or nothing at all.
    const loadingEl = document.createElement('div');
    loadingEl.className = 'claim-review-address-loading';
    loadingEl.style.display = 'none';

    const loadingSpinner = document.createElement('i');
    loadingSpinner.className = LOADING_ICON_CLASS;
    loadingSpinner.setAttribute('aria-hidden', 'true');
    loadingEl.appendChild(loadingSpinner);

    const loadingText = document.createElement('span');
    loadingText.textContent = LOADING_TEXT;
    loadingEl.appendChild(loadingText);

    section.appendChild(loadingEl);

    // Everything below (heading, help text, comparison grid, choice buttons)
    // only makes sense once a real suggestion has been found — wrapped
    // together so populateAddressComparison()/clearAddressComparison() can
    // show/hide it as one unit. Hidden by default: neither the loading
    // spinner nor the rate-limit notice (#961/#965) have anything to show
    // here, and showing stale/empty labels alongside either one is
    // confusing (#965).
    const comparisonContent = document.createElement('div');
    comparisonContent.className = 'claim-review-comparison-content';
    comparisonContent.style.display = 'none';
    section.appendChild(comparisonContent);

    const heading = document.createElement('h4');
    heading.textContent = 'Suggested Address Correction';
    comparisonContent.appendChild(heading);

    const helpText = document.createElement('p');
    helpText.className = 'help-text';
    helpText.textContent = 'MelissaData suggests a slightly different shipping address. Choose which one you’d like to use:';
    comparisonContent.appendChild(helpText);

    const grid = document.createElement('div');
    grid.className = 'claim-review-comparison-grid';

    const enteredCol = document.createElement('div');
    enteredCol.className = 'claim-review-comparison-col';
    const enteredHeading = document.createElement('h5');
    enteredHeading.textContent = 'What You Entered';
    const enteredList = document.createElement('dl');
    enteredList.className = 'claim-review-comparison-entered';
    enteredCol.appendChild(enteredHeading);
    enteredCol.appendChild(enteredList);

    const suggestedCol = document.createElement('div');
    suggestedCol.className = 'claim-review-comparison-col';
    const suggestedHeading = document.createElement('h5');
    suggestedHeading.textContent = 'What MelissaData Suggests';
    const suggestedList = document.createElement('dl');
    suggestedList.className = 'claim-review-comparison-suggested';
    suggestedCol.appendChild(suggestedHeading);
    suggestedCol.appendChild(suggestedList);

    grid.appendChild(enteredCol);
    grid.appendChild(suggestedCol);
    comparisonContent.appendChild(grid);

    const actions = document.createElement('div');
    actions.className = 'claim-review-address-actions text-center';

    // Static markup only (never user data) — safe to build with
    // createElement/appendChild, matching the Submit button's spinner-icon
    // pattern above. Shown/hidden purely via CSS (`.is-selected
    // .claim-review-selected-icon`) as a redundant, colorblind-safe signal
    // alongside the CSS highlight (#936).
    const useOriginalIcon = document.createElement('i');
    useOriginalIcon.className = 'fa-solid fa-check claim-review-selected-icon';
    useOriginalIcon.setAttribute('aria-hidden', 'true');

    const useOriginalBtn = document.createElement('button');
    useOriginalBtn.type = 'button';
    useOriginalBtn.className = 'button hollow claim-review-use-original';
    useOriginalBtn.setAttribute('aria-pressed', 'false');
    useOriginalBtn.appendChild(useOriginalIcon);
    useOriginalBtn.appendChild(document.createTextNode('Use my address as entered'));

    const useCorrectedIcon = document.createElement('i');
    useCorrectedIcon.className = 'fa-solid fa-check claim-review-selected-icon';
    useCorrectedIcon.setAttribute('aria-hidden', 'true');

    const useCorrectedBtn = document.createElement('button');
    useCorrectedBtn.type = 'button';
    useCorrectedBtn.className = 'button claim-review-use-corrected';
    useCorrectedBtn.setAttribute('aria-pressed', 'false');
    useCorrectedBtn.appendChild(useCorrectedIcon);
    useCorrectedBtn.appendChild(document.createTextNode('Use suggested address'));

    actions.appendChild(useOriginalBtn);
    actions.appendChild(useCorrectedBtn);
    comparisonContent.appendChild(actions);

    summaryEl.insertAdjacentElement('beforebegin', section);

    // Build the returned object FIRST, then set `.setChoice` on it
    // referencing the same object — see the cached-element branch above for
    // why (#936).
    const comparisonEls = {
        section,
        loadingEl,
        loadingIcon: loadingSpinner,
        loadingText,
        comparisonContent,
        enteredList,
        suggestedList,
        useOriginalBtn,
        useCorrectedBtn,
        entered: null,
        suggested: null,
        loadingShowTimer: null,
    };
    comparisonEls.setChoice = buildSetChoice(form, useOriginalBtn, useCorrectedBtn, summaryEl, comparisonEls);

    useOriginalBtn.addEventListener('click', () => comparisonEls.setChoice('original'));
    useCorrectedBtn.addEventListener('click', () => comparisonEls.setChoice('corrected'));

    return comparisonEls;
}

/**
 * Show the "Checking your address…" placeholder, but only if the check is
 * still pending ~250ms from now — avoids a flash-of-loading-state on fast
 * connections where validateAddress() resolves almost immediately.
 */
function showAddressValidationLoading(comparisonEls) {
    clearTimeout(comparisonEls.loadingShowTimer);
    comparisonEls.loadingShowTimer = setTimeout(() => {
        comparisonEls.loadingEl.style.display = 'flex';
        comparisonEls.section.style.display = 'block';
    }, 250);
}

/**
 * Cancel any pending show-timer and hide the placeholder — called right
 * before both the success and error paths hand off to
 * populateAddressComparison()/clearAddressComparison(), so the placeholder
 * never lingers alongside (or after) the real result.
 */
function hideAddressValidationLoading(comparisonEls) {
    clearTimeout(comparisonEls.loadingShowTimer);
    comparisonEls.loadingShowTimer = null;
    comparisonEls.loadingEl.style.display = 'none';
}

/**
 * #961: the one exception to "every validateAddress() failure fails
 * silently" (see checkAddressForCorrection() below) — when the failure is
 * specifically the session rate limit, repurpose the same loading element
 * (swap the spinner for a static info icon, swap the text) rather than
 * hiding it, so the visitor sees a soft explanation instead of the
 * placeholder just vanishing. Persists until the next Review click resets
 * it via clearAddressComparison() below — no auto-hide timer, so there's no
 * race against how long it takes the visitor to read it.
 */
function showAddressValidationRateLimitNotice(comparisonEls) {
    clearTimeout(comparisonEls.loadingShowTimer);
    comparisonEls.loadingShowTimer = null;
    comparisonEls.loadingIcon.className = RATE_LIMIT_ICON_CLASS;
    comparisonEls.loadingText.textContent = RATE_LIMIT_TEXT;
    comparisonEls.loadingEl.style.display = 'flex';
    comparisonEls.section.style.display = 'block';
}

/**
 * #974: same repurposing pattern as showAddressValidationRateLimitNotice()
 * above, for the case where validateAddress() reports
 * `reason: 'captcha_pending'` — the Turnstile token either hasn't been
 * submitted to Offers/verifyCaptcha yet or hasn't been processed server-side
 * yet. Persists until the next Review click resets it via
 * clearAddressComparison() below, matching the rate-limit notice's
 * lifecycle exactly.
 */
function showCaptchaPendingNotice(comparisonEls) {
    clearTimeout(comparisonEls.loadingShowTimer);
    comparisonEls.loadingShowTimer = null;
    comparisonEls.loadingIcon.className = CAPTCHA_PENDING_ICON_CLASS;
    comparisonEls.loadingText.textContent = CAPTCHA_PENDING_TEXT;
    comparisonEls.loadingEl.style.display = 'flex';
    comparisonEls.section.style.display = 'block';
}

function clearAddressComparison(comparisonEls) {
    hideAddressValidationLoading(comparisonEls);
    // Restore the loading element's default (spinner) content in case the
    // rate-limit notice (#961) left it showing the info icon/text instead.
    comparisonEls.loadingIcon.className = LOADING_ICON_CLASS;
    comparisonEls.loadingText.textContent = LOADING_TEXT;
    comparisonEls.section.style.display = 'none';
    // #965: the heading/help-text/grid/buttons only make sense alongside a
    // real suggestion — hide them so neither the (already-hidden) section
    // nor a later loading/notice state can show stale labels underneath.
    comparisonEls.comparisonContent.style.display = 'none';
    comparisonEls.enteredList.innerHTML = '';
    comparisonEls.suggestedList.innerHTML = '';
    comparisonEls.useOriginalBtn.classList.remove('is-selected');
    comparisonEls.useCorrectedBtn.classList.remove('is-selected');
    comparisonEls.useOriginalBtn.removeAttribute('aria-pressed');
    comparisonEls.useCorrectedBtn.removeAttribute('aria-pressed');
    // Reset so a later Review click with no correction found can't leak a
    // stale override into a fresh populateSummary() call (#936).
    comparisonEls.entered = null;
    comparisonEls.suggested = null;
}

function appendComparisonRow(list, label, value) {
    const dt = document.createElement('dt');
    dt.textContent = label;

    const dd = document.createElement('dd');
    dd.textContent = value || '—';

    const row = document.createElement('div');
    row.className = 'claim-review-comparison-row is-diff';
    row.appendChild(dt);
    row.appendChild(dd);
    list.appendChild(row);
}

/**
 * Populate the comparison section with only the fields that actually
 * differ, default the choice to "corrected" (matching the backend's default
 * when no choice is posted at all — see submitClaim()), and show it.
 */
function populateAddressComparison(comparisonEls, diffs, entered, suggested) {
    comparisonEls.enteredList.innerHTML = '';
    comparisonEls.suggestedList.innerHTML = '';

    diffs.forEach(({ key, label }) => {
        appendComparisonRow(comparisonEls.enteredList, label, entered[key]);
        appendComparisonRow(comparisonEls.suggestedList, label, suggested[key]);
    });

    // Must be set BEFORE setChoice('corrected') below, since that closure
    // reads comparisonEls.entered/.suggested to re-render the summary (#936).
    comparisonEls.entered = entered;
    comparisonEls.suggested = suggested;

    comparisonEls.setChoice('corrected');
    comparisonEls.comparisonContent.style.display = 'block';
    comparisonEls.section.style.display = 'block';
}

/**
 * Fire the single, session-cached MelissaData check for this Review click
 * (see Offers/validateAddress, #906) and, if it finds a real difference,
 * populate the comparison UI. Never blocks or delays the modal opening —
 * see initClaimReviewModal() below for why this intentionally isn't awaited.
 *
 * Bulletproof by design, with ONE exception (#961): any non-success
 * response, thrown error, or network failure reaches onError, which clears/
 * hides the comparison section — the exact same state as "no difference
 * found" — EXCEPT a rate-limited response, which shows a soft inline notice
 * instead so the visitor knows why the correction assist isn't available.
 * A MelissaData outage (or any other failure) can never block or otherwise
 * affect submission either way.
 */
function checkAddressForCorrection(form, reviewBtn, comparisonEls) {
    const entered = buildEnteredAddress(form);

    showAddressValidationLoading(comparisonEls);

    validateAddress({
        payload: entered,
        endpoint: '/Offers/validateAddress',
        button: reviewBtn,
        onSuccess: data => {
            hideAddressValidationLoading(comparisonEls);

            const suggested = data.validated_address || {};
            const diffs = diffAddressFields(entered, suggested);

            if (!diffs.length) {
                clearAddressComparison(comparisonEls);
                return;
            }

            populateAddressComparison(comparisonEls, diffs, entered, suggested);
        },
        onError: err => {
            if (err?.rateLimited) {
                showAddressValidationRateLimitNotice(comparisonEls);
                return;
            }

            if (err?.captchaPending) {
                showCaptchaPendingNotice(comparisonEls);
                return;
            }

            hideAddressValidationLoading(comparisonEls);
            clearAddressComparison(comparisonEls);
        },
    });
}

/**
 * Create (once) the fallback validation message element and insert it
 * immediately after the Review button. Uses Foundation's own `form-error`
 * convention (see ViewHelper::formError()/fieldError() in claimForm.php)
 * rather than inventing new styling — `.form-error` is hidden by default and
 * `.is-visible` reveals it in Foundation's error red.
 *
 * Idempotent: if called more than once on the same button (shouldn't happen
 * in practice since initClaimReviewModal() only runs once per page), it
 * reuses the existing element instead of appending a duplicate.
 */
function ensureValidationMessage(reviewBtn) {
    let messageEl = reviewBtn.nextElementSibling;
    if (messageEl && messageEl.classList.contains('claim-review-validation-message')) {
        return messageEl;
    }

    messageEl = document.createElement('span');
    messageEl.className = 'form-error claim-review-validation-message';
    messageEl.textContent = 'Please fill in all required fields before reviewing your request.';
    reviewBtn.insertAdjacentElement('afterend', messageEl);
    return messageEl;
}

/**
 * #974: Turnstile's widget calls this by name (via the `data-callback`
 * attribute on the `.cf-turnstile` div — see claimForm.php) the moment it
 * verifies the visitor, passing the challenge token. Immediately POSTs the
 * token to Offers/verifyCaptcha so the session flag
 * (`offer_captcha_verified_at`) is set server-side before the visitor even
 * clicks Review, in the common case.
 *
 * Nothing visible needs to happen on success — the session flag is now set
 * and both validateAddress() and submitClaim() will see it. On failure, no
 * special UI action is taken here either: the visitor will simply see the
 * `captcha_pending` notice (see showCaptchaPendingNotice() above) later if
 * they try to Review before verification succeeds.
 *
 * A global function (rather than a module export used directly) because
 * Turnstile's `data-callback` attribute requires a global function name, not
 * a module import — there's no existing global-callback pattern elsewhere in
 * this codebase to match, so `window.onOfferCaptchaVerified` below is a new
 * but necessary exception to the module-scoped convention.
 *
 * @param {string} token
 */
/**
 * #1079: show/hide the security-check failure notice under the Review button
 * (`#claim-captcha-error` — see claimForm.php). Bails silently when the
 * element isn't present, so pages without the claim form (or with CAPTCHA
 * disabled) are unaffected.
 *
 * @param {boolean} visible
 */
function setCaptchaLoadFailureNotice(visible) {
    const el = document.getElementById('claim-captcha-error');
    if (!el) return;

    el.textContent = visible ? CAPTCHA_LOAD_FAILED_TEXT : '';
    el.style.display = visible ? 'block' : 'none';
}

/**
 * #1079: Turnstile invokes this by name via `data-error-callback` /
 * `data-timeout-callback` on the `.cf-turnstile` div (see claimForm.php).
 *
 * Before this existed, a widget that failed to render produced no callback
 * anyone handled: the visitor saw nothing (the widget is interaction-only,
 * so invisible by design), the `offer_captcha_verified_at` session flag was
 * never set, and every submission attempt was rejected server-side with no
 * way for them to recover. Surfacing the failure is the whole point — the
 * gate itself is unchanged, and this deliberately does NOT allow submission.
 *
 * Global for the same reason onOfferCaptchaVerified() is: Turnstile's
 * callback attributes take a global function name, not a module import.
 *
 * @param {string} [code] Turnstile's error code, when it supplies one.
 */
export function onOfferCaptchaError(code) {
    log('Turnstile widget error/timeout', code);
    setCaptchaLoadFailureNotice(true);
}

/**
 * #1079: start the blocked-script watchdog for the claim form's verification
 * widget. Called from initClaimReviewModal().
 *
 * Only arms when the page actually renders a `.cf-turnstile` widget (CAPTCHA
 * enabled) and only reports when, after the timeout, the Turnstile API is
 * still absent from `window` — i.e. its script never executed. If the script
 * did load, its own error/timeout callbacks own the failure reporting and
 * this stays quiet.
 */
function startCaptchaScriptWatchdog() {
    const widget = document.querySelector('.cf-turnstile');
    if (!widget) return;

    window.setTimeout(() => {
        if (window.turnstile) return;
        log('Turnstile script never loaded');
        setCaptchaLoadFailureNotice(true);
    }, CAPTCHA_SCRIPT_LOAD_TIMEOUT_MS);
}

export function onOfferCaptchaVerified(token) {
    // Clear any failure notice the watchdog or an earlier error callback put
    // up (#1079): a late-but-successful verification means the visitor is no
    // longer blocked, so the message must not linger and misinform them.
    setCaptchaLoadFailureNotice(false);

    const csrfToken =
        document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content') ?? '';

    const body = new URLSearchParams();
    body.set('cf-turnstile-response', token);
    body.set('_csrf_token', csrfToken);

    fetch('/Offers/verifyCaptcha', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-CSRF-Token': csrfToken,
        },
        body: body.toString(),
    })
        .then(resp => resp.json())
        .then(json => {
            log('Offers/verifyCaptcha result', json);
        })
        .catch(err => {
            logError(err);
        });
}

if (typeof window !== 'undefined') {
    window.onOfferCaptchaVerified = onOfferCaptchaVerified;
    window.onOfferCaptchaError = onOfferCaptchaError;
}

/**
 * Wire up the offer claim form's Review modal.
 * Bails silently if the form/modal markup isn't present on the page.
 *
 * Idempotent (production incident, unreleased ticket following #942): unlike
 * ensureAddressComparisonSection()/ensureValidationMessage() further up,
 * this function itself previously had no guard against being called more
 * than once. reviewBtn/form/editBtn/submitBtn/overlay/document are never
 * recreated between calls, so a second call attached a second, fully
 * duplicate set of listeners on top of the first — a single Submit click
 * then called form.requestSubmit() twice, racing two navigations to the
 * same URL. A claim was confirmed saved correctly server-side (DB + logs)
 * while the visitor never saw the Thank You page, landing back on the
 * plain claim form with no error instead — consistent with two competing
 * navigations resolving unpredictably. Marking the form once solves this
 * regardless of what causes a second call (this file cannot control every
 * way a caller might invoke it — e.g. any future duplicate script
 * inclusion or re-run of the page's init sequence).
 */
export function initClaimReviewModal() {
    const form = document.getElementById('claim-form');
    const reviewBtn = document.getElementById('claim-review-btn');
    const overlay = document.getElementById('claim-review-overlay');
    const modal = document.getElementById('claim-review-modal');

    if (!form || !reviewBtn || !overlay || !modal) return;
    if (form.dataset.claimReviewModalInitialized === 'true') return;
    form.dataset.claimReviewModalInitialized = 'true';

    const summaryEl = modal.querySelector('.claim-review-summary');
    const editBtn = modal.querySelector('.claim-review-edit');
    const submitBtn = modal.querySelector('.claim-review-submit');
    const validationMessage = ensureValidationMessage(reviewBtn);

    // #1079: arm the blocked-script watchdog once per form init (guarded by
    // the claimReviewModalInitialized check above, so never doubled up).
    startCaptchaScriptWatchdog();

    const comparisonEls = summaryEl ? ensureAddressComparisonSection(modal, summaryEl, form) : null;

    // Tracks whether form.requestSubmit() is currently in flight, so the
    // backdrop-click and Escape dismiss paths can be suppressed while the
    // submit button is showing its disabled/spinner state (see #879).
    let isSubmitting = false;

    const hideValidationMessage = () => {
        validationMessage.classList.remove('is-visible');
    };

    const open = () => {
        hideValidationMessage();
        populateSummary(form, summaryEl);
        overlay.style.display = 'block';
        modal.style.display = 'block';
    };

    const close = () => {
        overlay.style.display = 'none';
        modal.style.display = 'none';
        reviewBtn.focus();
    };

    reviewBtn.addEventListener('click', () => {
        if (!form.reportValidity()) {
            // Native reportValidity() shows a browser tooltip near the first
            // invalid field, but that's easy to miss (scroll position,
            // browser/OS rendering quirks). Add an explicit, guaranteed-
            // visible fallback on top of it: scroll to and focus the first
            // invalid field ourselves, and show a message that doesn't rely
            // on native tooltip rendering at all.
            const firstInvalid = form.querySelector(':invalid');
            if (firstInvalid) {
                if (typeof firstInvalid.scrollIntoView === 'function') {
                    firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                firstInvalid.focus();
            }
            validationMessage.classList.add('is-visible');
            return;
        }

        // Reset any prior comparison state before checking again — a stale
        // comparison/choice from a previous Review click (before the visitor
        // used Edit to change a field) must never leak into this one.
        if (comparisonEls) clearAddressComparison(comparisonEls);

        // Fire the (single, session-cached) MelissaData check, then open the
        // modal right away rather than waiting on the network round-trip —
        // this is advisory-only and must never delay or block the review
        // step. checkAddressForCorrection() updates the comparison UI in
        // place once the check resolves (typically well under a second);
        // any failure/timeout leaves the comparison cleared/hidden, i.e. the
        // exact same state as today's plain summary.
        if (comparisonEls) checkAddressForCorrection(form, reviewBtn, comparisonEls);

        open();
    });

    // Clearing strategy: hide the fallback message as soon as the user starts
    // correcting *any* field, rather than waiting for the next Review click.
    // This gives immediate feedback that progress is being made, matching
    // how the native browser tooltip already disappears on input.
    form.addEventListener('input', hideValidationMessage);

    if (editBtn) {
        editBtn.addEventListener('click', close);
    }

    if (submitBtn) {
        submitBtn.addEventListener('click', () => {
            // The Review flow already validated the form via reportValidity()
            // before this modal ever opened, so this should always pass. Stay
            // defensive anyway: only enter the loading state if the form is
            // still actually valid, so a fluke failure can't leave the modal
            // stuck disabled forever with no way to recover.
            if (!form.checkValidity()) {
                close();
                form.reportValidity();
                return;
            }

            // Keep the modal + overlay open (do NOT call close()) so the user
            // sees feedback instead of the form going blank while the request
            // is in flight. Disable both actions to prevent double-submits or
            // reopening the edit view mid-submit.
            isSubmitting = true;
            if (editBtn) editBtn.disabled = true;
            submitBtn.disabled = true;
            submitBtn.textContent = 'Submitting…';

            const spinner = document.createElement('i');
            spinner.className = 'fa-solid fa-spinner fa-spin';
            spinner.style.marginLeft = '0.4rem';
            submitBtn.appendChild(spinner);

            form.requestSubmit();
        });
    }

    overlay.addEventListener('click', () => {
        if (!isSubmitting) close();
    });

    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && modal.style.display === 'block' && !isSubmitting) {
            close();
        }
    });
}
